import * as React from "react"
import styled from "styled-components"
import { ChildrenProps } from "../../@types/@core"
import { HEADER_HEIGHT_S } from "../../constants/"
import { breakpoint, breakpoints, colors, scale } from "../../styles"
import { Flex, LayoutProps } from "../Flex"
import { Icon } from "../Icon"
import { Text, TextProps } from "../Text"

export interface DrawerProps {
	isOpened?: boolean
	header?: React.ReactNode
	minBreakpoint?: keyof typeof breakpoints
	title?: string
	zIndex?: number
	onClose?: () => void
	children?: React.ReactNode
}

interface DrawerContextValue {
	minBreakpoint?: keyof typeof breakpoints
	onClose?: () => void
}

export const DrawerContext = React.createContext<DrawerContextValue>({})

export interface DrawerCloseButtonProps {
	onClose?: () => void
}

export interface DrawerComponent {
	Header: React.FC<ChildrenProps>
	Title: React.FC<ChildrenProps>
	CloseButton: React.FC<DrawerCloseButtonProps>
	Content: React.FC<LayoutProps>
}

const Wrapper = styled.div<DrawerProps>`
	${(props) => breakpoint("_", props.minBreakpoint!)`
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	transition: background 0.3s, transform 0.3s;
	background: ${props.isOpened ? "rgba(0, 0, 0, 0.3)" : "rgba(0, 0, 0, 0)"};
	pointer-events: ${props.isOpened ? "all" : "none"};
	z-index: ${props.zIndex};
`}
`

const DrawerStyled = styled.div<DrawerProps>`
	${(props) => breakpoint("_", props.minBreakpoint!)`
		display: flex;
		justify-content: space-between;
		flex-direction: column;
		width: 100%;
		max-width: none;
		background: white;
		height: 100%;
		overflow: auto;
		transition: transform 0.3s;
		transform: translateX(${props.isOpened ? 0 : "100%"});

		@media (min-width: ${scale.px(53)}) {
			max-width: ${scale.px(53)};
			margin-left: calc(100% - ${scale.px(53)});
		}
	`}
`

const CloseButton = styled.button``

const Header = styled.div<DrawerProps>`
	display: none;
	padding: 0 ${scale.px(3)};
	border-bottom: 1px solid ${colors["blue33"]};
	justify-content: space-between;
	align-items: center;
	height: ${scale.px(HEADER_HEIGHT_S)};

	${CloseButton} {
		margin-left: auto;
	}

	${(props) => breakpoint("_", props.minBreakpoint!)`
		display: flex;
	`}
`

// Allows clicks on outside of Drawer to close it
const OutsideButton = styled.button<DrawerProps>`
	display: none;
	position: absolute;
	top: 0;
	left: 0;
	width: calc(100% - ${scale.px(53)});
	height: 100%;
	outline: none;

	${(props) => breakpoint("_", props.minBreakpoint!)`
		display: block;
	`}

	@media (max-width: ${scale.px(53)}) {
		display: none;
	}
`

const Drawer: React.FC<DrawerProps> & DrawerComponent = ({
	children,
	...rest
}) => (
	<DrawerContext.Provider
		value={{ minBreakpoint: rest.minBreakpoint, onClose: rest.onClose }}
	>
		<Wrapper {...rest}>
			<DrawerStyled {...rest}>{children}</DrawerStyled>
			<OutsideButton onClick={rest.onClose} {...rest} />
		</Wrapper>
	</DrawerContext.Provider>
)

Drawer.CloseButton = ((props) => {
	const context = React.useContext(DrawerContext)
	const onClose = props.onClose || context.onClose

	return (
		<CloseButton onClick={onClose}>
			<Icon icon="close" color="navy" />
		</CloseButton>
	)
}) as React.FC<DrawerCloseButtonProps>

Drawer.Header = ((props: { children?: React.ReactNode }) => {
	const { minBreakpoint } = React.useContext(DrawerContext)
	return <Header minBreakpoint={minBreakpoint}>{props.children}</Header>
}) as React.FC

Drawer.Title = (props) => (
	<Text
		fontSize={2}
		lineHeight={3}
		fontWeight="bold"
		textColor="navy"
		mb={0}
		{...props}
	/>
)

Drawer.Content = (props) => (
	<Flex flexDirection="column" w={1} overflow="auto" flex={1}>
		<Flex display="block" overflow="auto" {...props} />
	</Flex>
)

Drawer.defaultProps = {
	isOpened: false,
	onClose: () => {},
}

export { Drawer }
